import { isDefined } from "~/shared/common";
import { NumberFormat, PropertyKind, SizeFormat, StatusKind } from "~/shared/enums";
import type {
  Property,
  PropertyDefaultAssignees,
  PropertyDefaultAttachments,
  PropertyDefaultCompletedAt,
  PropertyDefaultCreatedAt,
  PropertyDefaultCreatedBy,
  PropertyDefaultDartboard,
  PropertyDefaultDates,
  PropertyDefaultDescription,
  PropertyDefaultKind,
  PropertyDefaultPriority,
  PropertyDefaultReviewers,
  PropertyDefaultSize,
  PropertyDefaultStatus,
  PropertyDefaultTags,
  PropertyDefaultTimeTracking,
  PropertyDefaultTitle,
  PropertyDefaultUpdatedAt,
  PropertyDefaultUpdatedBy,
  PropertyUpdate,
  Status,
} from "~/shared/types";
import { makeDuid } from "~/utils/common";
import { propertyComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToProperties: Map<string, Property>;
  _sortedPropertyDuids: string[];
};

export const state: State = {
  _duidsToProperties: new Map(),
  _sortedPropertyDuids: [],
};

export type Getters = {
  propertyList: Property[];
  getPropertyByDuid: (duid: string) => Property | undefined;
  getPropertyByKind: (kind: PropertyKind) => Property[];
  defaultKindProperty: PropertyDefaultKind;
  defaultTitleProperty: PropertyDefaultTitle;
  defaultDescriptionProperty: PropertyDefaultDescription;
  defaultDartboardProperty: PropertyDefaultDartboard;
  defaultStatusProperty: PropertyDefaultStatus;
  defaultAssigneesProperty: PropertyDefaultAssignees;
  defaultDatesProperty: PropertyDefaultDates;
  defaultPriorityProperty: PropertyDefaultPriority;
  defaultTagsProperty: PropertyDefaultTags;
  defaultSizeProperty: PropertyDefaultSize;
  defaultTimeTrackingProperty: PropertyDefaultTimeTracking;
  defaultReviewersProperty: PropertyDefaultReviewers;
  defaultAttachmentsProperty: PropertyDefaultAttachments;
  defaultCreatedAtProperty: PropertyDefaultCreatedAt;
  defaultCreatedByProperty: PropertyDefaultCreatedBy;
  defaultUpdatedAtProperty: PropertyDefaultUpdatedAt;
  defaultUpdatedByProperty: PropertyDefaultUpdatedBy;
  defaultCompletedAtProperty: PropertyDefaultCompletedAt;
};

export type Actions = {
  createProperty: (
    kind: PropertyKind,
    order: string,
    partialProperty?: Partial<Property>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Property>;
  updateProperty: (update: PropertyUpdate, options?: { awaitBackend?: boolean }) => Promise<Property | undefined>;
  deleteProperty: (property: Property, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setProperties: (properties: Property[]) => void;
  $createOrUpdateProperty: (property: Property) => void;
  $deleteProperty: (property: Property) => void;
};

const findPropertyOrThrow = <T extends PropertyKind>(
  dataStore: { propertyList: Property[] },
  kind: T
): Property & { kind: T } => {
  const res = dataStore.propertyList.find((e): e is Property & { kind: T } => e.kind === kind);
  if (!res) {
    // TODO reenable when we we figure out the root cause of all of the noise
    // throw new Error(`No ${kind} property found`);
    return {
      duid: `@fallback/${kind}`,
      kind,
      order: "",
      hidden: true,
      title: kind
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      description: "",
      adtl: {},
    } as Property & { kind: T };
  }
  return res;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  propertyList() {
    return this._sortedPropertyDuids.map((e) => this._duidsToProperties.get(e)).filter(isDefined);
  },
  getPropertyByDuid() {
    return (duid) => this._duidsToProperties.get(duid);
  },
  getPropertyByKind() {
    return (kind) => this.propertyList.filter((e) => e.kind === kind);
  },
  defaultKindProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_KIND);
  },
  defaultTitleProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_TITLE);
  },
  defaultDescriptionProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_DESCRIPTION);
  },
  defaultDartboardProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_DARTBOARD);
  },
  defaultStatusProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_STATUS);
  },
  defaultAssigneesProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_ASSIGNEES);
  },
  defaultDatesProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_DATES);
  },
  defaultPriorityProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_PRIORITY);
  },
  defaultTagsProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_TAGS);
  },
  defaultSizeProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_SIZE);
  },
  defaultTimeTrackingProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_TIME_TRACKING);
  },
  defaultReviewersProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_REVIEWERS);
  },
  defaultAttachmentsProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_ATTACHMENTS);
  },
  defaultCreatedAtProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_CREATED_AT);
  },
  defaultCreatedByProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_CREATED_BY);
  },
  defaultUpdatedAtProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_UPDATED_AT);
  },
  defaultUpdatedByProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_UPDATED_BY);
  },
  defaultCompletedAtProperty() {
    return findPropertyOrThrow(this, PropertyKind.DEFAULT_COMPLETED_AT);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createProperty(kind, order, partialProperty, options = {}) {
    const { awaitBackend = false } = options;
    const property: Property = {
      duid: makeDuid(),
      kind,
      order,
      hidden: true,
      title: "Property",
      description: "",
      adtl: {},
      ...partialProperty,
    } as Property;
    if (
      (property.kind === PropertyKind.DEFAULT_ASSIGNEES ||
        property.kind === PropertyKind.DEFAULT_REVIEWERS ||
        property.kind === PropertyKind.USER) &&
      property.adtl.isMultiple === undefined
    ) {
      property.adtl.isMultiple = false;
    }
    if (property.kind === PropertyKind.DEFAULT_DATES || property.kind === PropertyKind.DATES) {
      if (property.adtl.isRange === undefined) {
        property.adtl.isRange = false;
      }
      if (property.adtl.showDuration === undefined) {
        property.adtl.showDuration = false;
      }
    }
    if (property.kind === PropertyKind.NUMBER && property.adtl.format === undefined) {
      property.adtl.format = NumberFormat.INTEGER;
    }
    if (property.kind === PropertyKind.DEFAULT_SIZE && property.adtl.format === undefined) {
      property.adtl.format = SizeFormat.TSHIRT;
    }

    this.$createOrUpdateProperty(property);

    let newDefaultStatuses: Status[] | undefined;
    if (property.kind === PropertyKind.STATUS) {
      const unstartedStatus: Status = {
        duid: makeDuid(),
        propertyDuid: property.duid,
        kind: StatusKind.UNSTARTED,
        locked: true,
        order: String.fromCharCode(2),
        title: "To-do",
        colorHex: "#c2c2c2",
        description: "",
      };
      const finishedStatus: Status = {
        duid: makeDuid(),
        propertyDuid: property.duid,
        kind: StatusKind.FINISHED,
        locked: true,
        order: String.fromCharCode(2),
        title: "Done",
        colorHex: "#4da56b",
        description: "",
      };

      newDefaultStatuses = [unstartedStatus, finishedStatus];
      this.$createOrUpdateStatus(unstartedStatus);
      this.$createOrUpdateStatus(finishedStatus);
    }

    const backendAction = this.$backend.property.create(property, newDefaultStatuses);
    if (awaitBackend) {
      await backendAction;
    }
    return property;
  },
  async updateProperty(update, options = {}) {
    const { awaitBackend = false } = options;
    const property = this.getPropertyByDuid(update.duid);
    if (!property) {
      return undefined;
    }

    Object.assign(property, update);
    updateInSorted(this._duidsToProperties, this._sortedPropertyDuids, property.duid, propertyComparator);

    const backendAction = this.$backend.property.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return property;
  },
  async deleteProperty(property, options = {}) {
    const { awaitBackend = false } = options;

    this.$cleanupDartboardsPropertyReferencesNoBackend([property.duid]);
    this.$cleanupFormFieldsDefaultsNoBackend([], [property.duid], { removeAllValues: true });
    this.$cleanupLayoutsFiltersNoBackend([property.duid], [property.duid], { removeAllFilters: true });
    this.$cleanupLayoutsKindConfigMapNoBackend([property.duid]);
    this.$cleanupLayoutsPropertySortsNoBackend([property.duid]);
    this.$cleanupTasksPropertyValuesNoBackend([], [property.duid], { removeAllValues: true });
    this.$cleanupViewsPropertyReferencesNoBackend([property.duid]);

    this.formFieldList.filter((e) => e.propertyDuid === property.duid).forEach((field) => this.$deleteFormField(field));

    this.$deleteProperty(property);

    const backendAction = this.$backend.property.delete(property.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setProperties(properties) {
    setObjectMapAndSorted(this._duidsToProperties, this._sortedPropertyDuids, properties, propertyComparator);
  },
  $createOrUpdateProperty(property) {
    const currentProperty = this.getPropertyByDuid(property.duid);

    if (!currentProperty) {
      this._duidsToProperties.set(property.duid, property);
    } else {
      Object.assign(currentProperty, property);
    }
    updateInSorted(this._duidsToProperties, this._sortedPropertyDuids, property.duid, propertyComparator);
  },
  $deleteProperty(property) {
    this._duidsToProperties.delete(property.duid);
    removeFromSorted(this._sortedPropertyDuids, property.duid);
  },
};

export { actions, getters };
